import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface PageContentBlock {
  section_key: string;
  field_key: string;
  field_type: string;
  field_value: string | null;
  field_json: unknown;
  is_active: boolean;
  language: string;
}

interface UsePageContentOptions {
  pageKey: string;
  language?: string;
  enabled?: boolean;
}

/**
 * Phase 6 — Page Content Manager hook.
 * Fetches active content blocks for a given page_key. Always returns a
 * `getContent(sectionKey, fieldKey, fallback)` helper that safely returns
 * the fallback if the override is missing, inactive, empty, or fetch fails.
 *
 * Lightweight & non-blocking: callers always render fallback first, then
 * upgrade to the override once data arrives. Never throws.
 */
export function usePageContent({ pageKey, language = "en", enabled = true }: UsePageContentOptions) {
  const [blocks, setBlocks] = useState<PageContentBlock[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error: err } = await supabase
          .from("page_content_blocks")
          .select("section_key, field_key, field_type, field_value, field_json, is_active, language")
          .eq("page_key", pageKey)
          .eq("language", language)
          .eq("is_active", true);
        if (cancelled) return;
        if (err) {
          setError(err.message);
          setBlocks([]);
        } else {
          setBlocks((data ?? []) as unknown as PageContentBlock[]);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Unknown error");
        setBlocks([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pageKey, language, enabled]);

  const map = useMemo(() => {
    const m = new Map<string, PageContentBlock>();
    for (const b of blocks) m.set(`${b.section_key}.${b.field_key}`, b);
    return m;
  }, [blocks]);

  function getContent(sectionKey: string, fieldKey: string, fallback: string): string {
    const b = map.get(`${sectionKey}.${fieldKey}`);
    if (!b) return fallback;
    const v = (b.field_value ?? "").trim();
    return v.length > 0 ? v : fallback;
  }

  return { getContent, loading, error, blocks };
}

export default usePageContent;
